import { useEffect, useId, useRef, useState } from 'react';
import type { TargetInfo } from './api';
import { ProductIdentity } from './ProductIdentity';

type Props = { targets: TargetInfo[]; targetId: string; onSwitch: (id: string) => void; recentStorageKey?: string };

function readRecent(key?: string): string[] {
  if (!key) return [];
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter((id): id is string => typeof id === 'string'))].slice(0, 5) : [];
  } catch { return []; }
}

export function TargetSwitcher({ targets, targetId, onSwitch, recentStorageKey }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState(() => readRecent(recentStorageKey));
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const selected = targets.find(target => target.id === targetId);
  const search = query.trim().toLowerCase();
  const matches = targets.filter(target => `${target.label} ${target.repo || ''} ${target.id}`.toLowerCase().includes(search));
  const recentTargets = !search ? recent.flatMap(id => matches.filter(target => target.id === id && id !== 'deployer-sandbox')) : [];
  const groups = [
    { label: 'Recent apps', targets: recentTargets },
    { label: 'Apps', targets: matches.filter(target => target.id !== 'deployer-sandbox' && !recentTargets.includes(target)) },
    { label: 'Test targets', targets: matches.filter(target => target.id === 'deployer-sandbox') },
  ].filter(group => group.targets.length);
  const options = groups.flatMap(group => group.targets);
  const activeTarget = options[Math.min(active, options.length - 1)];

  useEffect(() => { setRecent(readRecent(recentStorageKey)); }, [recentStorageKey]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  useEffect(() => {
    if (open && activeTarget) document.getElementById(`${listId}-${activeTarget.id}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeTarget?.id, listId]);

  function choose(id: string) {
    if (!targets.some(target => target.id === id)) return;
    const next = [id, ...recent.filter(previous => previous !== id && targets.some(target => target.id === previous))].slice(0, 5);
    setRecent(next);
    if (recentStorageKey) {
      try { window.localStorage.setItem(recentStorageKey, JSON.stringify(next)); } catch { /* Storage may be unavailable in private browsing. */ }
    }
    setOpen(false);
    trigger.current?.focus();
    onSwitch(id);
  }
  function identity(target: TargetInfo) {
    return <div className="target-switcher-identity"><ProductIdentity product={target} placement="sidebar" />{target.repo && <span className="target-switcher-repo">{target.repo}</span>}</div>;
  }
  if (!selected) return null;
  if (targets.length < 2) return <div className="target-switcher-static">{identity(selected)}</div>;
  return <div className="target-picker" ref={root}>
    <button type="button" className="target-switcher-trigger" data-testid="target-switcher" ref={trigger} aria-label={`Switch app. ${selected.label}${selected.repo ? `. ${selected.repo}` : ''}`} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined} onKeyDown={event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault(); setQuery(''); setActive(0); setOpen(true);
      }
    }} onClick={() => { setQuery(''); setActive(0); setOpen(value => !value); }}>
      {identity(selected)}<span className="target-switcher-chevron" aria-hidden>⌄</span>
    </button>
    {open && <div className="target-picker-popover">
      <input ref={input} className="target-picker-search" role="combobox" aria-label="Search apps" aria-autocomplete="list" aria-expanded="true" aria-controls={listId} aria-activedescendant={activeTarget ? `${listId}-${activeTarget.id}` : undefined} placeholder="Search apps or org/repo" value={query} onChange={event => { setQuery(event.target.value); setActive(0); }} onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current?.focus(); }
        if (event.key === 'Tab') setOpen(false);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          setActive(index => options.length ? (Math.min(index, options.length - 1) + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length : 0);
        }
        if (event.key === 'Enter') { event.preventDefault(); if (activeTarget) choose(activeTarget.id); }
      }} />
      <div className="target-picker-options" role="listbox" aria-label="Apps" id={listId}>
        {groups.map(group => <div role="group" aria-label={group.label} key={group.label}>
          <div className="target-picker-group" aria-hidden>{group.label}</div>
          {group.targets.map(target => <div role="option" aria-selected={target.id === targetId} id={`${listId}-${target.id}`} key={target.id} data-testid={`target-option-${target.id}`} className={`target-picker-option${activeTarget?.id === target.id ? ' is-active' : ''}`} onMouseDown={event => event.preventDefault()} onMouseMove={() => setActive(options.indexOf(target))} onClick={() => choose(target.id)}>
            {identity(target)}{target.id === targetId && <span className="target-picker-check" aria-hidden>✓</span>}
          </div>)}
        </div>)}
      </div>
      {!options.length && <p className="target-picker-empty" role="status">No apps match “{query.trim()}”.</p>}
    </div>}
  </div>;
}
